import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ObjectTable,
  type ObjectTableProps,
} from 'components/ObjectTable/ObjectTable';
import type { IObjectTableFilter } from 'components/ObjectTable/ObjectTableFilter';
import { getInlineString, getText } from 'i18n/intl';
import {
  type TFindObjectsPayload,
  findMoreObjects,
  findObjectsByIds,
  objectMapSelector,
} from 'slices/object.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TTEObject, TType } from '@timeedit/types';
import { Modal, type ModalProps } from '@timeedit/ui-components/antd';
import useFetchMissingItems from '../../pages/objectManager/hooks/useFetchMissingItems';

export type SelectObjectsModalProps = {
  /** Whether the modal is open */
  open: boolean;

  /** Width of the modal */
  width?: string | number;

  /** Callback function when the modal is closed, providing the selected objects if any */
  onClose?: (selectedObjects?: TTEObject[]) => void;

  /** List of available object types to include in the filter */
  availableObjectTypes?: TType[];

  /** List of object IDs that are already selected */
  selectedObjectIds?: number[];

  /**
   * Whether to fetch full TTEObject records for any `selectedObjectIds` that aren't yet in the
   * Redux objectMap. Defaults to `true` to preserve existing behavior. Set to `false` when the
   * preselect list can be very large — `findObjectsByIds` chunks 1000 ids per request and
   * awaits them sequentially, which is visible as a long modal-open hang on large lists. The
   * preselect remains visually correct for any preselected rows that happen to render in the
   * current view; rows the user never sees just don't get resolved into full objects.
   */
  prefetchSelected?: boolean;

  /** Filter selection instead of objects selection */
  selectFilter?: boolean;

  /** Default filter to apply when `selectFilter` is true */
  defaultFilter?: IObjectTableFilter;

  /** Callback function when the filter changes, providing the new filter */
  onFilterChange?: (filter?: IObjectTableFilter) => void;

  /** Text for the OK button */
  okText?: string;
};

const useFetchMissingObjects = (
  objectIds: number[],
  objectMap: Record<number, TTEObject>
) => {
  const dispatch = useAppDispatch();

  const fetchItemsFn = useCallback(
    (ids: number[]) => {
      dispatch(findObjectsByIds({ ids }));
    },
    [dispatch]
  );

  useFetchMissingItems({
    itemIds: objectIds,
    itemMap: objectMap,
    fetchItemsFn,
  });
};

export const useSelection = (
  objects: TTEObject[],
  defaultSelectedObjectIds?: number[],
  onChangeCallback?: ObjectTableProps['rowSelection']['onChange'],
  prefetchSelected: boolean = true
) => {
  const objectMap = useAppSelector(objectMapSelector);

  // This ensures that the full array of selected objects can be constructed.
  // Skipped when prefetchSelected is false (caller has determined the prefetch is too costly
  // and is fine with selectedObjects being limited to what's already loaded).
  useFetchMissingObjects(
    prefetchSelected ? (defaultSelectedObjectIds ?? []) : [],
    objectMap
  );

  const [selectedObjectIds, setSelectedObjectIds] = useState<number[]>(
    defaultSelectedObjectIds ?? []
  );
  const [selectedObjects, setSelectedObjects] = useState<TTEObject[]>([]);

  useEffect(() => {
    // Reset selection when the defaultSelectedObjectIds change
    setSelectedObjectIds(defaultSelectedObjectIds ?? []);

    setSelectedObjects(
      (defaultSelectedObjectIds ?? [])
        .map((id) => objectMap[id])
        .filter((obj): obj is TTEObject => obj != null)
    );
  }, [defaultSelectedObjectIds, objectMap, objects]);

  const onChange: ObjectTableProps['rowSelection']['onChange'] = useCallback(
    (selectedRowKeys, selectedRows, info) => {
      setSelectedObjectIds(selectedRowKeys as number[]);

      // The 'defaultSelectedObjectIds' are not guaranteed to be in the 'selectedRows' array as they might not be loaded yet
      // so we try to find them in the 'objects' array or the 'objectMap' if they are not in 'selectedRows'.
      // This ensures that we always have the full objects available in 'selectedObjects'.
      const allSelectedObjects = selectedRowKeys
        .map(
          (key, index) =>
            selectedRows[index] ??
            objects.find((obj) => obj.id === key) ??
            objectMap[key as number] ??
            null
        )
        .filter((row) => row != null);

      setSelectedObjects(allSelectedObjects);
      if (onChangeCallback) {
        onChangeCallback(selectedRowKeys, allSelectedObjects, info);
      }
    },
    [objects, objectMap, onChangeCallback]
  );

  const rowSelection: ObjectTableProps['rowSelection'] = useMemo(
    () => ({
      selectedRowKeys: selectedObjectIds,
      onChange,
      preserveSelectedRowKeys: true,
      hideSelectAll: true,
      columnWidth: 32,
    }),
    [selectedObjectIds, onChange]
  );

  return { rowSelection, selectedObjects };
};

const totalItemCount: ObjectTableProps['totalItemCount'] = async () => {
  // Fetch the total number of objects, but limit to 1 to avoid fetching a lot of data
  const response = await findMoreObjects({
    limit: 1,
  });
  return response.totalResults;
};

export const SelectObjectsModal = ({
  open,
  onClose,
  availableObjectTypes,
  selectedObjectIds,
  prefetchSelected = true,
  selectFilter = false,
  defaultFilter,
  onFilterChange,
  okText,
  width = '80vw',
}: SelectObjectsModalProps) => {
  const [objects, setObjects] = useState<TTEObject[]>([]);
  const [filter, setFilter] = useState<IObjectTableFilter | undefined>();
  const persistedFilterRef = useRef<IObjectTableFilter | undefined>(undefined);

  const resetFn: ObjectTableProps['resetFn'] = useCallback(() => {
    setObjects([]);
  }, []);

  const {
    rowSelection,
    selectedObjects,
  }: {
    rowSelection: ObjectTableProps['rowSelection'];
    selectedObjects: TTEObject[];
  } = useSelection(objects, selectedObjectIds, undefined, prefetchSelected);

  const fetchObjects: ObjectTableProps['fetchFn'] = useCallback(
    async (payload: TFindObjectsPayload) => {
      const response = await findMoreObjects(payload);

      setObjects((previousObjects) => {
        const newObjects = response?.results ?? [];
        return [...previousObjects, ...newObjects];
      });

      return response;
    },
    []
  );

  const onOk: ModalProps['onOk'] = useCallback(() => {
    if (selectFilter) {
      onFilterChange?.(filter);
    } else {
      onClose?.(selectedObjects);
    }
  }, [onClose, selectedObjects, filter, onFilterChange, selectFilter]);

  const onHandlerClose = useCallback(() => {
    persistedFilterRef.current = filter;
    resetFn();
    onClose?.();
  }, [onClose, resetFn, filter]);

  return (
    <Modal
      title={
        selectFilter
          ? getText('object_manager.adjust_filters_modal.title')
          : getText('object_manager.select_objects_modal.title')
      }
      destroyOnHidden
      open={open}
      onOk={onOk}
      okText={okText || 'OK'}
      onCancel={onHandlerClose}
      width={width}
    >
      <SelectObjects
        resetFn={resetFn}
        totalItemCount={totalItemCount}
        availableObjectTypes={availableObjectTypes}
        selectFilter={selectFilter}
        rowSelection={rowSelection}
        defaultFilter={persistedFilterRef.current ?? defaultFilter}
        filter={filter}
        setFilter={setFilter}
        objects={objects}
        fetchFn={fetchObjects}
      />
    </Modal>
  );
};

type SelectObjectsProps = {
  resetFn?: ObjectTableProps['resetFn'];
  totalItemCount?: ObjectTableProps['totalItemCount'];
  availableObjectTypes?: TType[];
  selectFilter?: boolean;
  defaultFilter?: IObjectTableFilter;
  rowSelection: ObjectTableProps['rowSelection'];
  filter?: IObjectTableFilter | undefined;
  setFilter: (filter?: IObjectTableFilter | undefined) => void;
  objects?: TTEObject[];
  fetchFn?: ObjectTableProps['fetchFn'];
};

export const SelectObjects = ({
  resetFn,
  totalItemCount,
  availableObjectTypes,
  selectFilter,
  defaultFilter,
  rowSelection,
  filter,
  setFilter,
  objects = [],
  fetchFn,
}: SelectObjectsProps) => {
  useEffect(() => {
    if (selectFilter) {
      setFilter(defaultFilter);
    }
  }, [selectFilter, defaultFilter, setFilter]);

  const footerContent: ObjectTableProps['footerContent'] = useMemo(() => {
    if (selectFilter) {
      return null;
    }

    return [
      getInlineString(
        'object_manager.object_table.footer.selected_items',
        rowSelection.selectedRowKeys.length
      ),
    ];
  }, [rowSelection.selectedRowKeys.length, selectFilter]);

  return (
    <ObjectTable
      tableId="objects.selectObjectsModal"
      objects={objects}
      fetchFn={fetchFn}
      resetFn={resetFn}
      footerContent={footerContent}
      totalItemCount={totalItemCount}
      scroll={{ y: '50vh' }}
      rowSelection={!selectFilter && rowSelection}
      availableObjectTypes={availableObjectTypes}
      onFilterChange={setFilter}
      defaultFilter={defaultFilter}
    />
  );
};
